import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Users, AlertCircle } from 'lucide-react';
import { getAllUsersApi } from '../../api/api';
import { showMessage } from '../../context/MessageContext.jsx';
import Card from '../../components/ui/Card.jsx';
import Input from '../../components/ui/Input.jsx';
import Pagination from '../../components/ui/Pagination.jsx';
import { usePagination } from '../../hooks/usePagination.js';
import './AllUsersList.css';

export default function AllUsersList() {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all'); // 'all', 'active', 'expired'

  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const limit = 50;
  const { totalPages, goToPage, goToNextPage, goToPreviousPage } = usePagination(
    totalCount,
    limit,
    currentPage,
    setCurrentPage
  );

  useEffect(() => {
    fetchUsers();
  }, [currentPage, search, statusFilter]);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const params = {
        page: currentPage,
        limit: limit,
        ...(search && { search }),
        ...(statusFilter !== 'all' && { status: statusFilter }),
      };
      const res = await getAllUsersApi(params);
      setUsers(res.data?.data || []);
      setTotalCount(res.data?.totalCount || 0);
    } catch (err) {
      showMessage('Failed to load users', 'error');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' });
    } catch {
      return dateString;
    }
  };

  const getStatusBadgeClass = (status) => {
    return status === 'Expired' ? 'expired' : 'active';
  };

  return (
    <div className="all-users-list">
      <div className="all-users-list-header">
        <div>
          <h1 className="all-users-list-title">All Users Overview</h1>
          <p className="all-users-list-subtitle">
            View all users across all organizations
          </p>
        </div>
      </div>

      <Card className="all-users-list-card">
        {/* Filters */}
        <div className="all-users-list-filters">
          <div className="all-users-list-search">
            <Search size={20} className="all-users-list-search-icon" />
            <Input
              type="text"
              placeholder="Search by name, ID, email, or phone..."
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setCurrentPage(1);
              }}
              className="all-users-list-search-input"
            />
          </div>

          <div className="all-users-list-status-filter">
            <label>Status:</label>
            <select
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value);
                setCurrentPage(1);
              }}
              className="all-users-list-status-select"
            >
              <option value="all">All</option>
              <option value="active">Active</option>
              <option value="expired">Expired</option>
            </select>
          </div>
        </div>

        {/* Table */}
        {loading ? (
          <div className="all-users-list-loading">Loading...</div>
        ) : users.length === 0 ? (
          <div className="all-users-list-empty">
            <Users size={48} />
            <p>No users found</p>
          </div>
        ) : (
          <>
            <div className="all-users-list-table-container">
              <table className="all-users-list-table">
                <thead>
                  <tr>
                    <th>User Name</th>
                    <th>ID</th>
                    <th>Email / Phone</th>
                    <th>Organization</th>
                    <th>Start Date</th>
                    <th>End Date</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((u) => {
                    const userId = u.tallyUserId || u.tallyUserId;
                    const userName = u.userName;
                    const userEmail = u.userEmail;
                    return (
                    <tr key={`${u.organizationTallyId}-${userId}`}>
                      <td>
                        <strong>{userName}</strong>
                      </td>
                      <td>{userId}</td>
                      <td>
                        <div>
                          {userEmail && (
                            <div>{userEmail}</div>
                          )}
                          {u.contactNumber && (
                            <div className="all-users-list-phone">{u.contactNumber}</div>
                          )}
                          {!userEmail && !u.contactNumber && 'N/A'}
                        </div>
                      </td>
                      <td>
                        <div>
                          <div>{u.organizationName}</div>
                          <div className="all-users-list-org-id">({u.organizationTallyId})</div>
                          {u.organizationUserAccessCount > 0 && (
                            <div className="all-users-list-limit-info">
                              Limit: {u.organizationUserAccessCount}
                            </div>
                          )}
                        </div>
                      </td>
                      <td>{formatDate(u.startDate)}</td>
                      <td>
                        {u.endDate ? (
                          <span className={u.accountStatus === 'Expired' ? 'all-users-list-expired-date' : ''}>
                            {formatDate(u.endDate)}
                          </span>
                        ) : (
                          'N/A'
                        )}
                      </td>
                      <td>
                        <span className={`all-users-list-badge ${getStatusBadgeClass(u.accountStatus)}`}>
                          {u.accountStatus === 'Expired' && <AlertCircle size={14} />}
                          {u.accountStatus}
                        </span>
                      </td>
                    </tr>
                  );
                  })}
                </tbody>
              </table>
            </div>

            {totalPages > 1 && (
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={goToPage}
                onNextPage={goToNextPage}
                onPreviousPage={goToPreviousPage}
              />
            )}
          </>
        )}
      </Card>
    </div>
  );
}

